import { useEffect, useState } from 'react';
import { IconAlert, IconClose } from './Icon';

// In-app replacement for `window.confirm`. Native confirm dialogs are
// browser-chrome (rendered as "chat.pgoc.ai says: …") which look out
// of place in an SPA — surfacing the same prompts via a styled modal
// keeps the experience consistent.
//
// Pattern matches ToastHost: a single host mounted at app root listens
// for an `app:confirm` custom event and resolves a stored callback
// with the user's choice. Anywhere can call:
//
//   if (!(await confirmDialog({ title: 'Delete?' }))) return;
//
// without prop drilling.

export interface ConfirmOptions {
  title: string;
  /** Optional secondary descriptive text. */
  description?: string;
  /** Defaults to "Confirm". */
  confirmLabel?: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
  /** Red confirm button styling for delete/leave/remove actions. */
  destructive?: boolean;
}

interface ConfirmEventDetail extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

const CONFIRM_EVENT = 'app:confirm';

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    const detail: ConfirmEventDetail = { ...opts, resolve };
    window.dispatchEvent(new CustomEvent<ConfirmEventDetail>(CONFIRM_EVENT, { detail }));
  });
}

interface PendingConfirm extends ConfirmOptions {
  id: number;
  resolve: (value: boolean) => void;
}

let nextId = 1;

export function ConfirmHost() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  useEffect(() => {
    function onConfirm(e: Event) {
      const ce = e as CustomEvent<ConfirmEventDetail>;
      if (!ce.detail) return;
      const id = nextId++;
      // If a confirm is already showing, queue this one by resolving
      // the new request with false. Two simultaneous confirms is a UX
      // smell — better to silently decline than stack modals.
      setPending((prev) => {
        if (prev) {
          ce.detail.resolve(false);
          return prev;
        }
        return { id, ...ce.detail };
      });
    }
    window.addEventListener(CONFIRM_EVENT, onConfirm);
    return () => window.removeEventListener(CONFIRM_EVENT, onConfirm);
  }, []);

  // Esc → cancel; Enter → confirm. Listening on the host (not just the
  // backdrop) so the keys work even if focus drifts.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        choose(false);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        choose(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  function choose(value: boolean) {
    setPending((prev) => {
      if (prev) prev.resolve(value);
      return null;
    });
  }

  if (!pending) return null;

  const confirmLabel = pending.confirmLabel ?? 'Confirm';
  const cancelLabel = pending.cancelLabel ?? 'Cancel';

  return (
    <div
      // Above the toast layer (60) so a confirm appears in front of any
      // queued toast notifications.
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 animate-fade-up"
      onClick={() => choose(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl glass-chrome hairline shadow-elevation overflow-hidden"
      >
        <header className="px-4 py-3 flex items-start gap-3 border-b border-zinc-800/80">
          <div
            className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
              pending.destructive
                ? 'bg-rose-500/15 text-rose-300'
                : 'bg-indigo-500/15 text-indigo-300'
            }`}
          >
            <IconAlert size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="confirm-title" className="text-sm font-semibold leading-snug">
              {pending.title}
            </h2>
            {pending.description && (
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed whitespace-pre-wrap">
                {pending.description}
              </p>
            )}
          </div>
          <button
            onClick={() => choose(false)}
            aria-label="Close"
            className="text-zinc-400 hover:text-zinc-100 transition"
          >
            <IconClose size={14} />
          </button>
        </header>
        <footer className="px-4 py-3 flex items-center justify-end gap-2">
          <button
            onClick={() => choose(false)}
            className="text-xs px-3 py-1.5 rounded-md text-zinc-400 hover:text-zinc-100 transition"
          >
            {cancelLabel}
          </button>
          <button
            // Auto-focus the confirm so Enter and Tab→Space both work for
            // keyboard-driven users.
            ref={(el) => el?.focus()}
            onClick={() => choose(true)}
            className={`text-xs px-3 py-1.5 rounded-md font-medium transition ${
              pending.destructive
                ? 'bg-rose-500 text-white hover:bg-rose-400'
                : 'bg-indigo-500 text-white hover:bg-indigo-400'
            }`}
          >
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
